import { apiClient } from '@/services/apiClient'
import {
  AmenityCategory,
  AmenityPOI,
  AmenitiesSearchParams,
  AmenitiesSearchResponse,
  AmenitiesData,
  CategoryScore,
  PoiResponse,
  AMENITY_CATEGORIES,
  DISTANCE_THRESHOLDS,
  WALKING_SPEED_MS,
  DRIVING_SPEED_MS,
} from '@popety-io/types'

class AmenitiesService {
  private cache: Map<
    string,
    { data: AmenitiesSearchResponse; timestamp: number }
  > = new Map()
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  /**
   * Search for POIs within a radius of a location
   */
  async searchPOIs(
    params: AmenitiesSearchParams
  ): Promise<AmenitiesSearchResponse> {
    try {
      const queryParams = new URLSearchParams({
        lat: params.latitude?.toString(),
        lng: params.longitude?.toString(),
        radius: params.radius?.toString(),
      })

      if (params.limit) {
        queryParams.append('limit', params.limit.toString())
      }

      const response = await apiClient.get(`/pois/search/radius?${queryParams}`)

      // Check if response has expected structure
      if (!response || !response.data) {
        console.warn('API returned empty or malformed response:', response)
        return {
          pois: [],
          total: 0,
          radius: params.radius,
          center: {
            latitude: params.latitude,
            longitude: params.longitude,
          },
        }
      }

      // Transform the raw API response to our expected format
      const poisArray: PoiResponse[] = Array.isArray(response.data)
        ? response.data
        : []

      // Convert API POIs to AmenityPOI format
      const transformedPois: AmenityPOI[] = poisArray.map((poi) => ({
        ...poi,
        id: poi.id,
        name: poi.name || 'Unknown',
        category: this.mapCategoryToAmenityCategory(poi.category),
        sub_category: poi.subcategory || 'Unknown', // Fixed: use sub_category instead of subCategory
        type: poi.type || 'Unknown',
        distance: poi.distance_meters || 0,
        coordinates: this.extractCoordinates(poi.geo_center),
      }))

      const searchResponse: AmenitiesSearchResponse = {
        pois: transformedPois,
        total: transformedPois.length,
        radius: params.radius,
        center: {
          latitude: params.latitude,
          longitude: params.longitude,
        },
      }

      return searchResponse
    } catch (error) {
      console.error('Failed to search POIs:', error)
      throw error
    }
  }

  /**
   * Process raw POI data into structured amenities data
   */
  processAmenitiesData(
    response: AmenitiesSearchResponse,
    filters?: { categories?: AmenityCategory[] }
  ): AmenitiesData {
    const categoryMap = new Map<AmenityCategory, AmenityPOI[]>()

    // Group POIs by category
    response?.pois?.forEach((poi) => {
      if (!filters?.categories || filters.categories.includes(poi.category)) {
        const pois = categoryMap.get(poi.category) || []
        pois.push(this.enrichPOI(poi))
        categoryMap.set(poi.category, pois)
      }
    })

    // Calculate category scores
    const categoryScores: CategoryScore[] = []
    let totalScore = 0
    let totalWeight = 0

    for (const [category, pois] of categoryMap.entries()) {
      const score = this.calculateCategoryScore(category, pois)
      const config = AMENITY_CATEGORIES[category]
      const subCategories = this.getSubCategories(category, pois)

      categoryScores.push({
        category,
        score,
        count: pois.length,
        sub_categories: subCategories,
        items: pois.sort((a, b) => a.distance - b.distance), // All POIs, sorted by distance
        expanded: false,
      })

      totalScore += score.value * config.weight
      totalWeight += config.weight
    }

    // Calculate overall livability score
    const overallScoreValue =
      totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0
    const overallScore = {
      value: overallScoreValue,
      max_value: 100,
      category: this.getScoreCategory(overallScoreValue),
      label: 'Livability Score',
      description: 'Based on proximity to essential amenities',
    }

    const processedData = {
      overall_score: overallScore,
      category_scores: categoryScores.sort((a, b) => {
        const aWeight = AMENITY_CATEGORIES[a.category].weight
        const bWeight = AMENITY_CATEGORIES[b.category].weight
        return bWeight - aWeight
      }),
      total_pois: response.total,
      search_radius: response.radius,
      walking_radius: Math.round(15 * 60 * WALKING_SPEED_MS), // 15 min walk
      center_point: response.center,
      last_updated: new Date(),
    }

    return processedData
  }

  /**
   * Enrich POI with calculated fields
   */
  private enrichPOI(poi: AmenityPOI): AmenityPOI {
    return {
      ...poi,
      walking_time: Math.round(poi.distance / WALKING_SPEED_MS / 60), // minutes
      driving_time: Math.round(poi.distance / DRIVING_SPEED_MS / 60), // minutes
    }
  }

  /**
   * Calculate score for a category based on POI proximity
   */
  private calculateCategoryScore(
    category: AmenityCategory,
    pois: AmenityPOI[]
  ): any {
    if (pois.length === 0) {
      return {
        value: 0,
        max_value: 100,
        category: 'low',
        label: `${AMENITY_CATEGORIES[category].label} Score`,
      }
    }

    // Score based on nearest POI and quantity
    const nearestDistance = Math.min(...pois.map((p) => p.distance))
    const proximityScore = this.getProximityScore(nearestDistance)
    const quantityScore = Math.min(100, pois.length * 10) // Max 100 for 10+ POIs

    // Weighted average (70% proximity, 30% quantity)
    const finalScore = Math.round(proximityScore * 0.7 + quantityScore * 0.3)

    return {
      value: finalScore,
      max_value: 100,
      category: this.getScoreCategory(finalScore),
      label: `${AMENITY_CATEGORIES[category].label} Score`,
    }
  }

  /**
   * Get proximity score based on distance
   */
  private getProximityScore(distance: number): number {
    if (distance <= DISTANCE_THRESHOLDS.excellent) return 100
    if (distance <= DISTANCE_THRESHOLDS.high) return 85
    if (distance <= DISTANCE_THRESHOLDS.medium) return 70
    if (distance <= DISTANCE_THRESHOLDS.low) return 50
    return 30
  }

  /**
   * Get score category
   */
  private getScoreCategory(
    score: number
  ): 'low' | 'medium' | 'high' | 'excellent' {
    if (score >= 85) return 'excellent'
    if (score >= 70) return 'high'
    if (score >= 50) return 'medium'
    return 'low'
  }

  /**
   * Extract subcategories from POIs
   */
  private getSubCategories(
    category: AmenityCategory,
    pois: AmenityPOI[]
  ): any[] {
    const subCategoryMap = new Map<string, number>()

    pois.forEach((poi) => {
      const count = subCategoryMap.get(poi.sub_category) || 0
      subCategoryMap.set(poi.sub_category, count + 1)
    })

    return Array.from(subCategoryMap.entries())
      .map(([name, count]) => ({
        id: `${category}-${name}`,
        name,
        parent_category: category, // Fixed: use parent_category to match AmenitySubCategory interface
        enabled: true,
        count,
      }))
      .sort((a, b) => b.count - a.count)
    // Show all subcategories, not just top 3, to ensure filtering works correctly
  }

  /**
   * Generate cache key
   */
  private getCacheKey(params: AmenitiesSearchParams): string {
    return `${params.latitude}-${params.longitude}-${params.radius}-${params.categories?.join(',') || 'all'}`
  }

  /**
   * Get from cache if valid
   */
  private getFromCache(key: string): AmenitiesSearchResponse | null {
    const cached = this.cache.get(key)
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data
    }
    return null
  }

  /**
   * Set cache
   */
  private setCache(key: string, data: AmenitiesSearchResponse): void {
    this.cache.set(key, { data, timestamp: Date.now() })

    // Clean old cache entries
    if (this.cache.size > 10) {
      const oldestKey = Array.from(this.cache.keys())[0]
      this.cache.delete(oldestKey)
    }
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear()
  }

  /**
   * Map backend category to AmenityCategory
   */
  private mapCategoryToAmenityCategory(
    backendCategory?: string
  ): AmenityCategory {
    if (!backendCategory) return 'services'

    // Map backend categories to our frontend categories
    const categoryMap: Record<string, AmenityCategory> = {
      Shopping: 'shopping',
      Grocery: 'grocery',
      'Culture and Entertainment': 'culture',
      'Dining and Drinking': 'dining',
      Education: 'education',
      Wellness: 'wellness',
      Healthcare: 'healthcare',
      Transport: 'transport',
      'Basic Amenities': 'services',
    }

    return categoryMap[backendCategory] || 'services'
  }

  /**
   * Extract coordinates from GeoJSON point
   */
  private extractCoordinates(geoCenter?: any): {
    latitude: number
    longitude: number
  } {
    if (geoCenter?.coordinates && Array.isArray(geoCenter.coordinates)) {
      return {
        longitude: geoCenter.coordinates[0] || 0,
        latitude: geoCenter.coordinates[1] || 0,
      }
    }
    return { latitude: 0, longitude: 0 }
  }
}

export const amenitiesService = new AmenitiesService()
